import { Alert, Container, Grid } from "@mui/material";
import LazyTable, { TableCellType } from "components/LazyTable";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { FormattedMessage as T, useIntl } from "react-intl";
import { ManualSubmissionFilter } from "services/KnowfullyAdminClient";
import { convertToUTC } from "utils/dateUtils";
import { SubmissionReportsFormValues } from "./AddCreditSubmissionReport";
import SubmissionReportsExporter from "./SubmissionReportsExporter";

const MAX_HEIGHT = 400;

export type ManualSubmissionPreviewTableProps = SubmissionReportsFormValues;

const SubmissionReportsPreviewTable = ({
  organizationId,
  authorityId,
  endDate,
  startDate,
  newEntriesOnly,
  removeInternalUsers,
}: ManualSubmissionPreviewTableProps) => {
  const { submissionService } = useGlobalContext();
  const { formatMessage: t } = useIntl();

  const filter = ManualSubmissionFilter.fromJS({
    organizationId,
    authorityId,
    endDate: endDate ? convertToUTC(endDate) : undefined,
    startDate: startDate ? convertToUTC(startDate) : undefined,
    includeSubmitted: !newEntriesOnly,
    removeInternalUsers,
  });

  const { data, isFetching, isFetched } = useStrictQuery(
    [
      "manualCreditSubmissionPreview",
      organizationId,
      authorityId,
      endDate,
      startDate,
      newEntriesOnly,
      removeInternalUsers,
    ],
    () => submissionService.previewManualSubmission(filter),
    { keepPreviousData: true }
  );

  const rows = (data?.items ?? []).map((x) => ({
    id: x.licenseNumber + x.courseNumber + x.completionDate,
    ...x,
  }));

  const cells = Object.keys(rows[0] ?? [])
    .filter((prop) => prop !== "id")
    .map(
      (prop) =>
        ({
          align: "left",
          width: 0,
          type: "text",
          noSort: true,
          accessor: prop,
          label: t({ id: prop }),
        } as TableCellType)
    );

  return (
    <Container>
      <LazyTable
        {...{
          rowCount: rows.length,
          rows,
          loading: isFetching,
          allowPagination: false,
          dense: true,
          cells,
          maxHeight: MAX_HEIGHT,
          stickyHeader: true,
          page: 0,
          order: "asc",
          orderBy: "",
          rowsPerPage: rows.length,
          setPage: () => {},
          setOrder: () => {},
          setOrderBy: () => {},
          setRowsPerPage: () => {},
        }}
      />

      {!!rows.length && (
        <Alert severity="warning" sx={{ my: 2 }}>
          <T id="creditSubmissionExportWarning" />
        </Alert>
      )}

      <Grid container alignItems="center" direction="column">
        <Grid item xs={12}>
          {!!rows.length && <SubmissionReportsExporter filter={filter} />}
          {isFetched && rows.length === 0 && (
            <Alert severity="info">
              <T id="nothingToExport" />
            </Alert>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default SubmissionReportsPreviewTable;
